import React from 'react';
import Button from './Button';

export const Card = ({ 
  children, 
  className = '', 
  variant = 'default',
  size = 'medium',
  interactive = false,
  elevated = false,
  ...props 
}) => {
  // Card variants — border colours signal semantic state
  const variants = {
    default: 'bg-surface border border-border-medium',
    primary: 'bg-info-light border border-info/40',
    success: 'bg-success-light border border-success/40',
    warning: 'bg-warning-light border border-warning/40',
    danger: 'bg-danger-light border border-danger/40',
    info: 'bg-info-light border border-info/40',
    muted: 'bg-surface-subtle border border-border',
    gradient: 'bg-gradient-to-br from-info-light to-primary-light border border-info/40'
  };

  const sizes = {
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8',
    xlarge: 'p-10'
  };

  // Brand-tinted shadow so cards pop off the blue-grey background
  const baseStyles = 'rounded-2xl shadow-brand-sm overflow-hidden transition-all duration-200';

  const interactiveStyles = interactive 
    ? 'cursor-pointer hover:shadow-brand-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2' 
    : '';

  const elevatedStyles = elevated 
    ? 'shadow-brand-md hover:shadow-brand-lg' 
    : '';

  return (
    <div
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${interactiveStyles} ${elevatedStyles} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// Specialized card components
export const CardHeader = ({ children, className = '', ...props }) => (
  <div className={`border-b border-border pb-4 mb-4 ${className}`} {...props}>
    {children}
  </div>
);

export const CardTitle = ({ children, className = '', level = 3, ...props }) => {
  const HeadingTag = `h${level}`;
  return (
    <HeadingTag className={`text-lg font-semibold text-text-primary mb-2 ${className}`} {...props}>
      {children}
    </HeadingTag>
  );
};

export const CardDescription = ({ children, className = '', ...props }) => (
  <p className={`text-sm text-text-secondary ${className}`} {...props}>
    {children}
  </p>
);

export const CardContent = ({ children, className = '', ...props }) => (
  <div className={`${className}`} {...props}>
    {children}
  </div>
);

export const CardFooter = ({ children, className = '', ...props }) => (
  <div className={`border-t border-border pt-4 mt-4 ${className}`} {...props}>
    {children}
  </div>
);

// Action card for clickable cards
export const ActionCard = ({ 
  children, 
  onClick, 
  className = '', 
  variant = 'default',
  size = 'medium',
  ...props 
}) => (
  <Card
    variant={variant}
    size={size}
    interactive={true}
    onClick={onClick}
    className={`group ${className}`}
    {...props}
  >
    {children}
  </Card>
);

// Stats card for displaying metrics
export const StatsCard = ({ 
  title, 
  value, 
  description, 
  subtitle,
  icon, 
  iconBg = 'bg-surface-subtle',
  trend,
  compact,
  /** Appended to value (CardTitle) — e.g. text-success, text-danger */
  valueClassName = '',
  /** Extra-tight mobile layout for inline KPI rows (e.g. session performance) */
  dense = false,
  className = '',
  ...props 
}) => {
  // Auto-responsive: compact on mobile unless explicitly overridden
  const isCompact = compact !== undefined ? compact : true;

  const valueSizeClass = dense
    ? '!text-base !leading-none sm:!text-2xl sm:!leading-none'
    : isCompact
      ? 'text-xl sm:text-2xl'
      : 'text-2xl';
  const titleLabelClass = dense
    ? '!text-[10px] uppercase tracking-wide !text-text-muted sm:!text-sm sm:normal-case sm:tracking-normal sm:!text-text-secondary'
    : isCompact
      ? 'text-xs sm:text-sm'
      : 'text-sm';
  const descClass = dense
    ? 'text-[10px] leading-tight text-text-muted line-clamp-2 sm:text-xs sm:leading-normal sm:line-clamp-none'
    : 'text-xs text-text-muted';
  const cardPadClass = dense ? '!p-2.5 sm:!p-4' : '';
  
  return (
    <Card size={isCompact ? 'small' : 'medium'} className={`text-center ${cardPadClass} ${className}`} {...props}>
      {icon && (
        <div className={`flex justify-center ${isCompact ? 'mb-2' : 'mb-3'} ${dense ? 'sm:mb-2' : ''}`}>
          <div className={`${isCompact ? 'w-9 h-9 sm:w-10 sm:h-10 rounded-lg' : 'w-12 h-12 rounded-xl'} ${iconBg} flex items-center justify-center border border-border`}>
            {isCompact ? <span className="[&>svg]:w-4 [&>svg]:h-4 sm:[&>svg]:w-5 sm:[&>svg]:h-5">{icon}</span> : icon}
          </div>
        </div>
      )}
      <CardTitle level={4} className={`${valueSizeClass} font-bold text-text-primary mb-0.5 ${valueClassName}`}>
        {value}
      </CardTitle>
      <CardDescription className={`${titleLabelClass} font-medium mb-0.5 sm:mb-1`}>
        {title}
      </CardDescription>
      {(description || subtitle) && (
        <p className={descClass}>{description || subtitle}</p>
      )}
      {trend && (
        <div className={`text-xs font-medium mt-2 ${
          trend > 0 ? 'text-success-dark' : trend < 0 ? 'text-danger-dark' : 'text-text-secondary'
        }`}>
          {trend > 0 ? '↗' : trend < 0 ? '↘' : '→'} {Math.abs(trend)}%
        </div>
      )}
    </Card>
  );
};

// Feature card for showcasing features
export const FeatureCard = ({ 
  icon, 
  title, 
  description, 
  action,
  className = '',
  ...props 
}) => (
  <Card 
    variant="default" 
    className={`text-center group hover:shadow-lg transition-all duration-200 ${className}`}
    {...props}
  >
      <div className="flex justify-center mb-4">
      <div className="w-16 h-16 bg-surface-subtle rounded-2xl flex items-center justify-center text-text-secondary text-2xl group-hover:bg-primary-light transition-colors">
        {icon}
      </div>
    </div>
    <CardTitle level={3} className="mb-3">
      {title}
    </CardTitle>
    <CardDescription className="mb-6">
      {description}
    </CardDescription>
    {action && (
      <div className="mt-auto">
        {action}
      </div>
    )}
  </Card>
);

// Session card specifically for session displays
export const SessionCard = ({ 
  session, 
  onSignup, 
  onCancelSignup, 
  onViewDetails,
  userSignupStatus,
  className = '',
  ...props 
}) => {
  const isSignedUp = userSignupStatus?.signup_status === 'confirmed' || userSignupStatus?.signup_status === 'waitlist';
  const isOnWaitlist = userSignupStatus?.signup_status === 'waitlist';
  const isCancelled = session.session_status === 'cancelled';

  return (
    <Card 
      variant={isCancelled ? 'danger' : 'default'}
      className={`${className}`}
      {...props}
    >
      {isCancelled && (
        <div className="bg-danger text-text-inverse px-4 py-2 text-center text-sm font-medium -m-6 mb-6">
          ⚠️ This session has been cancelled
        </div>
      )}
      
      <CardHeader>
        <CardTitle level={3}>
          {session.name || session.club_session_setup?.session_name || 'Badminton Session'}
        </CardTitle>
        <div className="flex items-center gap-2">
          {session.is_public ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success-light text-success-dark">
              🌍 Public
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-info-light text-info-dark">
              🔒 Private
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-text-primary">Date:</span>
            <p className="text-text-secondary">{new Date(session.session_date).toLocaleDateString()}</p>
          </div>
          <div>
            <span className="font-medium text-text-primary">Time:</span>
            <p className="text-text-secondary">{new Date(session.session_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
          </div>
          <div>
            <span className="font-medium text-text-primary">Players:</span>
            <p className="text-text-secondary">
              {session.signup_selection === 'host_select'
                ? `Up to ${session.max_players || 16}`
                : `${session.current_signups || 0}/${session.max_players}`}
            </p>
          </div>
          <div>
            <span className="font-medium text-text-primary">Courts:</span>
            <p className="text-text-secondary">{session.club_session_setup?.number_of_courts || 'N/A'}</p>
          </div>
        </div>
      </CardContent>

      <CardFooter>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="medium"
            onClick={onViewDetails}
            className="flex-1"
          >
            View Details
          </Button>
          <Button
            variant={isSignedUp ? 'danger' : 'primary'}
            size="medium"
            onClick={isSignedUp ? onCancelSignup : onSignup}
            disabled={isCancelled}
            className="flex-1"
          >
            {isSignedUp ? 'Cancel' : 'Sign Up'}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default Card;
